import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, Edit, Delete, Search, Save, Close } from '@mui/icons-material';
import { apiService } from '../services/api';

const PAGE_SIZE = 15;

const Tags: React.FC = () => {
  const [tags, setTags] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  // Inline edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const fetchTags = async (q = '') => {
    setLoading(true);
    try {
      const res: any = await apiService.getArticleTags(q || undefined);
      setTags(res.data.tags || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTags(); }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchTags(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [search]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTagName.trim()) return;
    setSubmitting(true);
    try {
      const res: any = await apiService.createArticleTag(newTagName.trim());
      setTags((prev) => [...prev, res.data.tag].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTagName('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to create tag');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (tag: any) => {
    setEditingId(tag.id);
    setEditName(tag.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = async (id: number) => {
    if (!editName.trim()) return;
    setSavingEdit(true);
    try {
      const res: any = await apiService.updateArticleTag(id, editName.trim());
      setTags((prev) =>
        prev.map((t) => (t.id === id ? res.data.tag : t)).sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingId(null);
      setEditName('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to update tag');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteArticleTag(deleteTarget.id);
      setTags((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to delete tag');
    }
  };

  const totalPages = Math.ceil(tags.length / PAGE_SIZE);
  const paginated = tags.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showPagination = totalPages > 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Tags</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Create new tag */}
      <Card sx={{ mb: 3, p: 2 }}>
        <form onSubmit={handleCreate}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="New tag name (e.g. 'Drought Resistant')"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<Add />}
              disabled={submitting || !newTagName.trim()}
            >
              {submitting ? 'Adding…' : 'Add Tag'}
            </Button>
          </Box>
        </form>
      </Card>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search tags…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: '100%', maxWidth: 360 }}
        InputProps={{
          startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
      />

      {/* Tags table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tags found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      {editingId === t.id ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(t.id);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                          />
                          <IconButton size="small" onClick={() => handleSaveEdit(t.id)} disabled={savingEdit}>
                            <Save fontSize="small" color="primary" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEdit}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        t.name
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={t.slug} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      {editingId === t.id ? null : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => startEdit(t)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(t)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {showPagination && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Delete tag "{deleteTarget?.name}"?
            <br />
            Articles using this tag will keep the old slug in their data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tags;
